import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  PageStatus,
  Button,
  SkeletonBase,
  SkeletonLoader
} from "@unified-checkout/design-system";
import { CircleExclamation } from "@unified-checkout/design-system/icons/index";
import type { IButton } from "libs/design-system/src/types/Button/button";

import { useLangStore } from "@app/dashboard/store/lang";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import {
  getSingleDispute
} from "@app/dashboard/services/inner_layout/disputes/get-single-dispute";

import { Header } from "./sections/Header";
import { UploadedFileBox } from "./sections/UploadedFileBox";
import { CaseDetails } from "./sections/CaseDetails";
import { AmountDetails } from "./sections/AmountDetails";
import { CustomerDetails } from "./sections/CustomerDetails";
import { CaseHistory } from "./sections/CaseHistory";

import { Drawers } from "./sections/drawers";

import styles from "./single-dispute.module.scss";


const ACTIONS = {
  SUBMIT_EVIDENCE: 'submit_evidence',
  ACCEPT: 'accept'
} as const;


export const SingleDispute = () => {
  const { t } = useTranslation();
  const { lang } = useLangStore();
  const { id } = useParams<{ id: string }>();

  const [isDrawerOpen, setIsDrawerOpen] = useState({
    accept: false,
    challenge: false
  });

  const {
    isLoading,
    isRefetching,
    isError,
    data,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_SINGLE_DISPUTE, lang],
    queryFn: () => getSingleDispute(id as string),
    select: (resp) => resp.data,
    enabled: !!id,
    refetchOnMount: "always",
  });

  const {
    id: disputeId,
    status,
    status_label,
    created_at,
    due_date,
    reason_label,
    events,
    transaction_amount_cents,
    amount_cents,
    currency,
    customer_name,
    customer_email,
    customer_address,
    allowed_actions,
    attachments
  } = data || {};

  const actionBtns: IButton[] = !!allowed_actions?.length ?
    allowed_actions.map((action: any) => (
      action.name === ACTIONS.ACCEPT ?
        {
          size: 'xl',
          variant: 'contained',
          text: t('innerLayout.disputesPage.actions.acceptDispute'),
          onClickHandler: () => setIsDrawerOpen(prev => ({ ...prev, accept: true }))
        }
        :
        action.name === ACTIONS.SUBMIT_EVIDENCE ?
          {
            size: 'xl',
            variant: 'outlined',
            text: t('innerLayout.disputesPage.actions.challenge'),
            onClickHandler: () => setIsDrawerOpen(prev => ({ ...prev, challenge: true }))
          }
          :
          null
    )).filter(Boolean) : [];

  const isDetailsLoading = isLoading || isRefetching;


  return (
    <div className={styles["single-dispute-wrapper"]}>

      {
        isError ?
          <PageStatus
            status="error"
            title={t('innerLayout.errors.smthWentWrong')}
            description={t('innerLayout.errors.dataRetrievalErr')}
            icon={CircleExclamation}
          />
          :
          <>
            <Header
              isLoading={isDetailsLoading}
              disputeId={disputeId}
              status={status}
              statusLabel={status_label}
              createdAt={created_at}
            />

            <div className={styles["single-dispute-wrapper__content"]}>
              <div className={styles["single-dispute-wrapper__content__col"]}>
                {
                  isDetailsLoading ?
                    <>
                      <SkeletonLoader height="7.6rem" noOfRows={2} />
                      <SkeletonLoader height="13.8rem" noOfRows={4} />
                      <SkeletonLoader height="10.8rem" noOfRows={3} />
                    </>
                    :
                    <>
                      {
                        !!attachments?.length &&
                        <UploadedFileBox
                          label={t('innerLayout.disputesPage.disputeDetails.detailsBoxes.uploadedFiles.title')}
                          attachments={attachments}
                        />
                      }

                      <CaseDetails
                        disputeId={disputeId}
                        dueDate={due_date}
                        reason={reason_label}
                      />

                      {!!events.length && <CaseHistory events={events} />}
                    </>
                }
              </div>
              <div className={styles["single-dispute-wrapper__content__col"]}>
                {
                  isDetailsLoading ?
                    <>
                      <SkeletonLoader height="11.5rem" noOfRows={3} />
                      <SkeletonLoader height="15.5rem" noOfRows={4} />
                    </>
                    :
                    <>
                      <AmountDetails
                        isLoading={isDetailsLoading}
                        transactionAmount={transaction_amount_cents}
                        disputedAmount={amount_cents}
                        currency={currency}
                      />
                      <CustomerDetails
                        name={customer_name}
                        email={customer_email}
                        address={customer_address}
                      />
                    </>
                }
              </div>
            </div>

            {
              (isDetailsLoading || !!actionBtns.length) &&
              <div className={styles["single-dispute-wrapper__footer"]}>
                {isDetailsLoading ?
                  <>
                    <SkeletonBase height="3.375rem" width="8rem" />
                    <SkeletonBase height="3.375rem" width="10.8rem" />
                  </>
                  :
                  actionBtns.map((btn, index) => <Button {...btn} key={index} />)
                }
              </div>
            }

            <Drawers
              isOpen={isDrawerOpen}
              setIsDrawerOpen={setIsDrawerOpen}
              data={{
                disputeId: disputeId?.toString(),
                reason: reason_label,
                disputedAmount: amount_cents,
                currency,
              }}
            />
          </>
      }

    </div>
  )
}
